import { Component } from '@theme/component';
import { CartAddEvent } from '@theme/events';
import { decodeEntities } from '@ts/searchspring-client';
import { whenSwymReady, offSwymReady, SWYM_ADDED_EVENT, SWYM_REMOVED_EVENT } from '@ts/utility-swym';

/**
 * A Swym wishlist item, as returned by `_swat.fetch` / `fetchListDetails`.
 * Only the fields the grid needs are documented.
 *
 * @typedef {Object} SwymProduct
 * @property {number} empi - Shopify product id.
 * @property {number} epi - Shopify variant id.
 * @property {string} [dt] - Product title; may be HTML-entity-encoded.
 * @property {string} [title] - Product title (shared-list shape); may be HTML-entity-encoded.
 * @property {string} [du] - Absolute product url.
 * @property {string} [uri] - Product url (shared-list shape).
 * @property {string} [iu] - Image url.
 * @property {number} [pr] - Price, in the store's major currency unit (e.g. dollars).
 */

/**
 * @typedef {Object} TSWishlistRefs
 * @property {HTMLElement} [grid] - Grid container that receives hydrated cards.
 */

/** How long to wait for Swym before falling back to the empty state (ms). */
const LOAD_TIMEOUT_MS = 10000;

/**
 * Wishlist page grid (TS-269).
 *
 * Hydrates the server-rendered shell (sections/ts-wishlist.liquid) with the
 * shopper's Swym wishlist. The Swym Wishlist Plus app embed loads the SDK and
 * exposes `window._swat` + `window.SwymCallbacks`; we render cards from the
 * items Swym returns and wire up remove-from-wishlist.
 *
 * The host carries a `data-state` of `loading` | `empty` | `ready`. It starts
 * as `loading` (skeletons) — set in Liquid so there's no flash — and only
 * resolves to `empty` or `ready` once Swym answers, since Swym takes a few
 * seconds to hydrate on page load.
 *
 * Add to Cart (TS-270): each card's `[data-wishlist-atc]` button resolves the
 * product on click via `/products/{handle}.js`. A single-variant product is
 * added straight to cart (dispatching the theme's `CartAddEvent`, which updates
 * the cart bubble and slides out the mini-cart); a multi-variant product hands
 * off to the shared Quick View modal (TS-244) for option selection; a sold-out
 * product disables the button.
 *
 * Share (TS-272): the `[data-wishlist-share]` CTA generates a Swym share URL
 * (`generateSharedListURL`, works for guest + authenticated) and opens a modal
 * to copy it; opening that URL (`?hkey=`) renders the read-only shared view —
 * no account nav or Share/Save, titled "Wishlist - shared by [Name]" from the
 * list owner's Swym userinfo (TS-496).
 *
 * Save: the `[data-wishlist-save]` CTA (labeled with the customer email when
 * logged in, else "Save Wishlist") opens the Save modal. Its content is
 * server-rendered per auth state; logging out clears the Swym shopper context.
 *
 * @extends {Component<TSWishlistRefs>}
 */
class TSWishlist extends Component {
  /** @type {SwymProduct[]} */
  #products = [];

  /** @type {any} The Swym tracker (`window._swat`). */
  #swat = null;

  /** @type {Map<string, any>} Cached product JSON (`/products/{handle}.js`) per handle. */
  #productCache = new Map();

  /** @type {HTMLTemplateElement | null} Source for each card's Quick View trigger. */
  #qvTriggerTemplate = null;

  /** @type {HTMLElement | null} */
  #gridElement = null;

  /** @type {HTMLElement | null} */
  #titleElement = null;

  /** @type {HTMLElement | null} */
  #countElement = null;

  /** @type {HTMLElement | null} */
  #statusElement = null;

  /** @type {HTMLElement | null} */
  #shareButton = null;

  /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
  #shareDialog = null;

  /** @type {HTMLInputElement | null} */
  #shareUrlInput = null;

  /** @type {HTMLButtonElement | null} */
  #shareCopyButton = null;

  /** @type {HTMLElement | null} */
  #shareCopyLabel = null;

  /** @type {HTMLElement | null} */
  #saveButton = null;

  /** @type {(HTMLElement & { showDialog?: () => void }) | null} */
  #saveDialog = null;

  /** @type {HTMLAnchorElement | null} */
  #saveLogoutButton = null;

  /** @type {number} */
  #loadTimeout = 0;

  /** @type {string} */
  currency = 'USD';

  /** @type {string} */
  atcLabel = 'Add to bag';

  /** @type {string} */
  addingLabel = 'Adding…';

  /** @type {string} */
  addedLabel = 'Added';

  /** @type {string} */
  soldOutLabel = 'Sold out';

  /** @type {string} */
  errorLabel = 'Try again';

  /** @type {string} */
  removeLabel = 'Remove';

  /** @type {string} */
  loadingLabel = 'Loading';

  /** @type {string} */
  sharedTitle = 'Wishlist';

  /** @type {string} Shared-view title template; `__NAME__` is replaced with the list owner's name. */
  sharedByTitle = '';

  /** @type {string} */
  copyLabel = 'Copy URL';

  /** @type {string} */
  copiedLabel = 'Copied!';

  /** @type {boolean} */
  isShared = false;

  connectedCallback() {
    super.connectedCallback();

    this.#gridElement = this.querySelector('[data-wishlist-grid]');
    this.#titleElement = this.querySelector('[data-wishlist-title]');
    this.#countElement = this.querySelector('[data-wishlist-product-count]');
    this.#statusElement = this.querySelector('[data-wishlist-status]');
    this.#qvTriggerTemplate = /** @type {HTMLTemplateElement | null} */ (
      this.querySelector('template[data-wishlist-qv-trigger]')
    );
    this.currency = this.dataset.currency || 'USD';
    this.atcLabel = this.dataset.atcLabel || 'Add to bag';
    this.addingLabel = this.dataset.addingLabel || 'Adding…';
    this.addedLabel = this.dataset.addedLabel || 'Added';
    this.soldOutLabel = this.dataset.soldoutLabel || 'Sold out';
    this.errorLabel = this.dataset.errorLabel || 'Try again';
    this.removeLabel = this.dataset.removeLabel || 'Remove';
    this.loadingLabel = this.dataset.loadingLabel || 'Loading';
    this.sharedTitle = this.dataset.sharedTitle || 'Wishlist';
    this.sharedByTitle = this.dataset.sharedByTitle || '';
    this.isShared = this.hasAttribute('data-wishlist-shared') || this.#sharedListId() !== null;
    // Reflect the shared state as an attribute so the CSS below can hide the
    // owner-only account nav and Share/Save controls — covers the `?hkey=`
    // case, where Liquid hasn't set the attribute (it only knows the
    // shared-page handles). Correct the title too for that same case; Swym's
    // fetch upgrades it with the owner's name (#setSharedTitle).
    if (this.isShared) {
      this.setAttribute('data-wishlist-shared', '');
      if (this.#titleElement) this.#titleElement.textContent = this.sharedTitle;
    }

    this.#shareButton = this.querySelector('[data-wishlist-share]');
    this.#shareDialog = this.querySelector('[data-wishlist-share-modal]');
    this.#shareUrlInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-wishlist-share-url]'));
    this.#shareCopyButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-wishlist-share-copy]'));
    this.#shareCopyLabel = this.querySelector('[data-wishlist-share-copy-label]');
    if (this.#shareCopyButton) {
      this.copyLabel = this.#shareCopyButton.dataset.copyLabel || 'Copy URL';
      this.copiedLabel = this.#shareCopyButton.dataset.copiedLabel || 'Copied!';
    }
    this.#shareButton?.addEventListener('click', this.#openShareModal);
    this.#shareCopyButton?.addEventListener('click', this.#copyShareUrl);

    this.#saveButton = this.querySelector('[data-wishlist-save]');
    this.#saveDialog = this.querySelector('[data-wishlist-save-modal]');
    this.#saveLogoutButton = /** @type {HTMLAnchorElement | null} */ (this.querySelector('.ts-wishlist-save__logout'));
    this.#saveButton?.addEventListener('click', this.#openSaveModal);
    this.#saveLogoutButton?.addEventListener('click', this.#onSaveLogout);

    // Stay in the loading state (skeletons) until Swym answers, then resolve to
    // empty or ready. Fall back to empty if Swym never responds.
    this.#setState('loading');
    this.#loadTimeout = window.setTimeout(() => this.#handleLoadTimeout(), LOAD_TIMEOUT_MS);

    // Stay in sync with wishlist toggles made on cart/PDP buttons.
    document.addEventListener(SWYM_ADDED_EVENT, this.#onExternalAdd);
    document.addEventListener(SWYM_REMOVED_EVENT, this.#onExternalRemove);

    whenSwymReady(this.#onSwymReady);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.clearTimeout(this.#loadTimeout);
    document.removeEventListener(SWYM_ADDED_EVENT, this.#onExternalAdd);
    document.removeEventListener(SWYM_REMOVED_EVENT, this.#onExternalRemove);
    offSwymReady(this.#onSwymReady);
  }

  /**
   * A wishlist button added an item — append just its card (a full #render
   * would detach in-flight add-to-bag state and refetch every image). Skipped
   * for shared lists (someone else's list) and while loading (the fetch will
   * include it).
   * @param {Event} event
   */
  #onExternalAdd = (event) => {
    if (this.isShared || this.dataset.state === 'loading') return;
    const { product } = /** @type {CustomEvent} */ (event).detail ?? {};
    if (!product || this.#products.some((item) => Number(item.epi) === Number(product.epi))) return;
    this.#products.push(product);
    this.#gridElement?.append(this.#buildCard(product));
    this.#syncCountAndState();
  };

  /**
   * A wishlist button removed an item — drop just its card. (Our own
   * #removeItem also dispatches this; the some() guard makes that a no-op.)
   * @param {Event} event
   */
  #onExternalRemove = (event) => {
    if (this.isShared || this.dataset.state === 'loading') return;
    const epi = Number(/** @type {CustomEvent} */ (event).detail?.epi);
    if (!this.#products.some((item) => Number(item.epi) === epi)) return;
    this.#products = this.#products.filter((item) => Number(item.epi) !== epi);
    this.#gridElement?.querySelector(`[data-epi="${epi}"]`)?.remove();
    this.#syncCountAndState();
  };

  /** Keep the visible count and empty/ready state in step with #products. */
  #syncCountAndState() {
    if (this.#countElement) this.#countElement.textContent = String(this.#products.length);
    this.#setState(this.#products.length === 0 ? 'empty' : 'ready');
  }

  /** @param {any} swat - The Swym tracker (`window._swat`). */
  #onSwymReady = (swat) => {
    this.#swat = swat;
    this.#fetchProducts();
  };

  /**
   * Reads a shared-list id from the `?hkey=` query param, if present.
   * @returns {string | null}
   */
  #sharedListId() {
    return new URLSearchParams(window.location.search).get('hkey');
  }

  #fetchProducts() {
    if (!this.#swat) return;

    const sharedId = this.#sharedListId();

    if (sharedId) {
      this.#swat.fetchListDetails({ lid: sharedId }, (/** @type {any} */ response) => {
        this.isShared = true;
        this.#products = (response && response.items) || [];
        this.#setSharedTitle(response && response.list && response.list.userinfo);
        this.#render();
      });
      return;
    }

    this.#swat.fetch((/** @type {SwymProduct[]} */ products) => {
      this.#products = Array.isArray(products) ? products : [];
      this.#render();
    });
  }

  #render() {
    window.clearTimeout(this.#loadTimeout);

    const count = this.#products.length;
    if (this.#countElement) this.#countElement.textContent = String(count);

    if (this.#gridElement) {
      this.#gridElement.replaceChildren();
      for (const product of this.#products) {
        this.#gridElement.append(this.#buildCard(product));
      }
    }

    this.#setState(count === 0 ? 'empty' : 'ready');
  }

  /**
   * Title for the shared (read-only) view: "Wishlist - shared by [Name]" when
   * Swym knows the list owner (TS-496), else the plain shared title. The name
   * is another shopper's data, so it only ever lands via textContent.
   * @param {{ fname?: string, lname?: string } | undefined} userinfo
   */
  #setSharedTitle(userinfo) {
    if (!this.#titleElement) return;
    const name = [userinfo?.fname, userinfo?.lname].filter(Boolean).join(' ').trim();
    this.#titleElement.textContent =
      name && this.sharedByTitle ? this.sharedByTitle.replace('__NAME__', name) : this.sharedTitle;
  }

  /**
   * Sets the visible state and keeps the SR status / aria-busy in sync.
   * @param {'loading' | 'empty' | 'ready'} state
   */
  #setState(state) {
    this.dataset.state = state;
    this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    if (this.#statusElement) {
      this.#statusElement.textContent = state === 'loading' ? this.loadingLabel : '';
    }
  }

  /** Swym never responded — show the empty state rather than skeletons forever. */
  #handleLoadTimeout() {
    if (this.dataset.state === 'loading') this.#setState('empty');
  }

  /**
   * Builds a single wishlist card from Swym's stored product data.
   * @param {SwymProduct} product
   * @returns {HTMLElement}
   */
  #buildCard(product) {
    const rawUrl = product.du || product.uri || '';
    const handle = (rawUrl.split('?')[0] || '').split('/').filter(Boolean).pop() || '';
    // Swym stores `du` as an absolute URL to the live domain; keep only the
    // path so links/fetches hit the current theme rather than production.
    const url = this.#toSameOriginPath(rawUrl);
    // Swym's stored titles are HTML-entity-encoded (legacy PLP buttons seeded
    // `dt` from the raw Searchspring feed value); decode so the card shows the
    // same title as the PLP grid cards.
    const title = decodeEntities(product.dt || product.title || '');
    const price = typeof product.pr === 'number' ? this.#formatMoney(product.pr) : '';

    const card = document.createElement('article');
    card.className = 'ts-wishlist-card';
    card.setAttribute('role', 'listitem');
    card.dataset.productHandle = handle;
    // Lookup hook for surgical external add/remove (see #onExternalRemove).
    if (product.epi != null) card.dataset.epi = String(product.epi);

    // The skeleton carries NO shopper-controlled data — every value that comes
    // from Swym (url, image src, handle, title, price) is set via DOM APIs
    // below, never string-interpolated into markup. Shared lists (?hkey=)
    // render other shoppers' Swym data, so a crafted value (e.g. an image URL
    // engineered to break out of the attribute) must never reach innerHTML.
    card.innerHTML = `
      <a class="ts-wishlist-card__media" tabindex="-1" aria-hidden="true"></a>
      <div class="ts-wishlist-card__info">
        <div class="ts-wishlist-card__details">
          <h2 class="ts-wishlist-card__title">
            <a class="ts-wishlist-card__link"></a>
          </h2>
          <span class="ts-wishlist-card__price"></span>
        </div>
        <div class="ts-wishlist-card__actions">
          <button type="button" class="ts-wishlist-card__atc" data-wishlist-atc></button>
          ${this.isShared ? '' : '<button type="button" class="ts-wishlist-card__remove" data-wishlist-remove></button>'}
        </div>
      </div>
    `;

    const media = card.querySelector('.ts-wishlist-card__media');
    if (media) {
      media.setAttribute('href', url);
      const imageUrl = this.#safeImageUrl(product.iu || '');
      if (imageUrl) {
        const img = document.createElement('img');
        img.setAttribute('src', imageUrl);
        img.alt = title;
        img.loading = 'lazy';
        media.append(img);
      }
    }

    const link = card.querySelector('.ts-wishlist-card__link');
    if (link) {
      link.setAttribute('href', url);
      link.textContent = title;
    }

    const priceEl = card.querySelector('.ts-wishlist-card__price');
    if (priceEl) priceEl.textContent = price;

    const atcButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-wishlist-atc]'));
    if (atcButton) {
      atcButton.dataset.productHandle = handle;
      atcButton.textContent = this.atcLabel;
      atcButton.addEventListener('click', () => this.#handleAtc(product, url, atcButton));
    }

    const removeButton = /** @type {HTMLButtonElement | null} */ (card.querySelector('[data-wishlist-remove]'));
    if (removeButton) {
      removeButton.textContent = this.removeLabel;
      removeButton.addEventListener('click', () => this.#removeItem(product, card, removeButton));
    }

    const quickViewTrigger = this.#buildQuickViewTrigger(title);
    if (quickViewTrigger) card.append(quickViewTrigger);

    return card;
  }

  /**
   * @param {SwymProduct} product
   * @param {HTMLElement} card
   * @param {HTMLButtonElement} button
   */
  #removeItem(product, card, button) {
    if (!this.#swat || this.isShared) return;

    button.disabled = true;

    this.#swat.removeFromWishList(
      product,
      () => {
        this.#products = this.#products.filter((item) => item.epi !== product.epi);
        card.remove();
        this.#syncCountAndState();
        // Tell wishlist buttons elsewhere on the page to downgrade this epi.
        document.dispatchEvent(new CustomEvent(SWYM_REMOVED_EVENT, { detail: { epi: product.epi } }));
      },
      () => {
        button.disabled = false;
      }
    );
  }

  /**
   * Clone the shared Quick View trigger (TS-244) for a card, wrapped in a ghost
   * anchor that overlays the media so the "+" lands at its bottom-right. The
   * shared `ts-quick-view` controller handles the click (it resolves the URL
   * from the card's link); here we just personalize the accessible label.
   * @param {string} title
   * @returns {HTMLElement | null}
   */
  #buildQuickViewTrigger(title) {
    const template = this.#qvTriggerTemplate;
    const source = template?.content.querySelector('.ts-quick-view-trigger');
    if (!source) return null;

    const trigger = /** @type {HTMLElement} */ (source.cloneNode(true));
    const labelTemplate = trigger.getAttribute('data-label-quick-view');
    if (labelTemplate && title) trigger.setAttribute('aria-label', labelTemplate.replace('__NAME__', title));

    const anchor = document.createElement('div');
    anchor.className = 'ts-wishlist-card__quick-view-anchor';
    anchor.append(trigger);
    return anchor;
  }

  /**
   * Open the Share modal and populate it with the Swym share URL. Works for
   * guest and authenticated wishlists (AC5).
   */
  #openShareModal = () => {
    // On a shared (read-only) view the CTA is hidden, but guard here too: Share
    // acts on the viewer's own wishlist, not the list being viewed (AC5).
    if (this.isShared || !this.#swat || !this.#shareDialog) return;

    if (this.#shareUrlInput) this.#shareUrlInput.value = '';
    this.#setCopyLabel(this.copyLabel);
    this.#shareDialog.showDialog?.();

    this.#swat.generateSharedListURL(
      null,
      (/** @type {string} */ url) => {
        if (this.#shareUrlInput && url) this.#shareUrlInput.value = url;
      },
      () => {}
    );
  };

  /** Copy the generated share URL to the clipboard and confirm (AC3). */
  #copyShareUrl = async () => {
    const url = this.#shareUrlInput?.value || '';
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      this.#setCopyLabel(this.copiedLabel);
      window.setTimeout(() => this.#setCopyLabel(this.copyLabel), 2000);
    } catch {
      // Clipboard unavailable (e.g. insecure context) — select for manual copy.
      this.#shareUrlInput?.select();
    }
  };

  /** @param {string} label */
  #setCopyLabel(label) {
    if (this.#shareCopyLabel) this.#shareCopyLabel.textContent = label;
  }

  /**
   * Open the Save modal. Its content is server-rendered per auth state, so this
   * just opens the dialog (no Swym dependency).
   */
  #openSaveModal = () => {
    // Hidden on the shared (read-only) view; guard so a stray trigger can't
    // open the owner-only Save modal.
    if (this.isShared) return;
    this.#saveDialog?.showDialog?.();
  };

  /**
   * Clear the Swym shopper context before following the Shopify logout link, so
   * the saved-wishlist association is dropped immediately rather than lingering
   * until Swym next re-checks.
   * @param {Event} event
   */
  #onSaveLogout = (event) => {
    const href = this.#saveLogoutButton?.getAttribute('href');
    if (!this.#swat || typeof this.#swat.disconnectShopperContext !== 'function' || !href) return;

    event.preventDefault();
    const go = () => window.location.assign(href);
    this.#swat.disconnectShopperContext(go, go);
  };

  /**
   * Add-to-cart from a wishlist card. Swym only stores ids/price/image, so the
   * product is resolved lazily on click to learn its variants and stock:
   *   - sold out            → disable the button and relabel it
   *   - more than 1 variant → open Quick View (TS-244) for option selection
   *   - single variant      → add straight to cart
   *
   * @param {SwymProduct} product
   * @param {string} url - The product URL (used to open Quick View).
   * @param {HTMLButtonElement} button
   */
  async #handleAtc(product, url, button) {
    const handle = button.dataset.productHandle;
    if (!handle || button.disabled) return;

    const idleLabel = button.textContent;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.addingLabel;

    try {
      const data = await this.#fetchProduct(handle);
      const variants = data && Array.isArray(data.variants) ? data.variants : [];

      // Sold out — leave the button disabled with a sold-out label (AC4).
      if (!data || !data.available) {
        button.textContent = this.soldOutLabel;
        button.setAttribute('aria-busy', 'false');
        return;
      }

      // Needs option selection — hand off to the shared Quick View modal (AC3).
      // Use the product JSON's same-origin relative `url`, not Swym's stored
      // `du` (an absolute URL to the live domain, whose theme has no
      // `.product-information` for Quick View to extract).
      if (variants.length > 1) {
        this.#openQuickView(data.url || url, button);
        this.#resetAtcButton(button, idleLabel);
        return;
      }

      const variant = variants[0];
      if (!variant || variant.available === false) {
        button.textContent = this.soldOutLabel;
        button.setAttribute('aria-busy', 'false');
        return;
      }

      await this.#addVariantToCart(variant.id, data);
      button.textContent = this.addedLabel;
      button.setAttribute('aria-busy', 'false');
      window.setTimeout(() => this.#resetAtcButton(button, idleLabel), 2000);
    } catch (error) {
      console.error('[ts-wishlist] add to cart failed:', error);
      button.textContent = this.errorLabel;
      button.setAttribute('aria-busy', 'false');
      window.setTimeout(() => this.#resetAtcButton(button, idleLabel), 2000);
    }
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {string | null} label
   */
  #resetAtcButton(button, label) {
    button.textContent = label ?? this.atcLabel;
    button.disabled = false;
    button.setAttribute('aria-busy', 'false');
  }

  /**
   * Fetch (and cache) a product's JSON so we can read its variants and stock.
   * @param {string} handle
   * @returns {Promise<any | null>}
   */
  async #fetchProduct(handle) {
    if (this.#productCache.has(handle)) return this.#productCache.get(handle);

    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) return null;

    const data = await response.json();
    this.#productCache.set(handle, data);
    return data;
  }

  /**
   * Open the shared Quick View modal (TS-244) for a product. Falls back to the
   * PDP if the modal isn't on the page so the shopper can still pick options.
   * @param {string} url
   * @param {HTMLElement} trigger - Receives focus back when the modal closes.
   */
  #openQuickView(url, trigger) {
    const quickView = /** @type {any} */ (document.querySelector('ts-quick-view'));
    if (quickView && typeof quickView.openForUrl === 'function') {
      quickView.openForUrl(url, { trigger });
    } else if (url) {
      window.location.assign(url);
    }
  }

  /**
   * Add a variant to the cart and broadcast the theme's `CartAddEvent` so the
   * cart bubble, mini-cart contents, and drawer slide-out all update (AC5).
   * @param {number} variantId
   * @param {any} product - The fetched product JSON (for the product id).
   */
  async #addVariantToCart(variantId, product) {
    const body = new FormData();
    body.set('id', String(variantId));
    body.set('quantity', '1');

    // Request the cart-items sections so subscribers can morph in place rather
    // than re-fetch (mirrors product-form-component).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);
    if (sectionIds.length) body.set('sections', sectionIds.join(','));

    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body,
    });
    const result = await response.json();
    if (result.status) throw new Error(result.description || result.message || 'cart add error');

    // Fetch the cart for an accurate total (drives the bubble + SR count).
    let cart = null;
    try {
      const cartResponse = await fetch('/cart.js');
      if (cartResponse.ok) cart = await cartResponse.json();
    } catch {
      cart = null;
    }

    // With an accurate total, set the bubble to it. Otherwise fall back to the
    // product-form's increment-by-one semantics so the count stays sane.
    const countData = cart
      ? { source: 'ts-wishlist-component', itemCount: cart.item_count }
      : { source: 'product-form-component', itemCount: 1 };

    this.dispatchEvent(
      new CartAddEvent(cart ?? undefined, this.id || 'ts-wishlist', {
        ...countData,
        productId: String(product.id),
        variantId: String(variantId),
        sections: result.sections,
      })
    );
  }

  /**
   * Convert a Swym-stored URL (usually an absolute URL to the live domain) to a
   * same-origin path, so card links and Quick View fetches resolve against the
   * current theme rather than production.
   * @param {string} value
   * @returns {string}
   */
  #toSameOriginPath(value) {
    if (!value) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      return parsed.pathname + parsed.search;
    } catch {
      return value;
    }
  }

  /**
   * Guard for Swym-stored image URLs. Only an absolute `https://` URL or a
   * root-relative path is allowed; anything else (a `javascript:`/`data:`
   * scheme, or a value crafted to inject markup) is dropped and the card
   * renders without an image. Defense-in-depth alongside `setAttribute`, since
   * shared lists (?hkey=) render image URLs from other shoppers' Swym data.
   * @param {string} value
   * @returns {string}
   */
  #safeImageUrl(value) {
    return /^(https:\/\/|\/)/.test(value) ? value : '';
  }

  /** @param {number} amount - Price in the store's major currency unit. */
  #formatMoney(amount) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(amount);
    } catch {
      return `$${amount.toFixed(2)}`;
    }
  }
}

if (!customElements.get('ts-wishlist')) {
  customElements.define('ts-wishlist', TSWishlist);
}
